import type { UserIdentity } from "@supabase/supabase-js";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { Notice, Progress } from "../components/Notice";
import { ProviderButton } from "../components/ProviderButtons";
import { api, ApiError, type Me } from "../lib/api";
import {
  PROVIDER_LABEL,
  SOCIAL_PROVIDERS,
  enabledProviders,
  linkProvider,
  type SocialProvider,
} from "../lib/authProviders";
import { supabase } from "../lib/supabase";

const FUTURE: Array<{ key: string; name: string }> = [
  { key: "control4", name: "Control4" },
  { key: "heos", name: "Denon HEOS" },
  { key: "blustream", name: "Blustream" },
];

function normalizeCode(raw: string) {
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8);
}

export function Account({ initialCode = "" }: { initialCode?: string }) {
  const { session, signOut } = useAuth();
  const [params] = useSearchParams();
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState(normalizeCode(initialCode || params.get("code") || ""));
  const [linking, setLinking] = useState(false);
  const [linkResult, setLinkResult] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const [identities, setIdentities] = useState<UserIdentity[] | null>(null);
  const [providers, setProviders] = useState<Set<SocialProvider>>(new Set());
  const [identityBusy, setIdentityBusy] = useState<string | null>(null);
  const [identityNote, setIdentityNote] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  // ?linked=<provider> is only a hint from the callback. The success
  // message is shown once the account's identities, fetched from Supabase,
  // actually include that provider; otherwise the visitor is told it is
  // not connected. Unknown values are ignored.
  const linkedHint = params.get("linked");
  const linkedProvider = linkedHint && linkedHint in PROVIDER_LABEL ? (linkedHint as SocialProvider) : null;
  const [linkedChecked, setLinkedChecked] = useState(false);

  const loadIdentities = useCallback(() => {
    if (!supabase) return;
    supabase.auth
      .getUserIdentities()
      .then(({ data, error }) => setIdentities(error ? [] : data?.identities ?? []))
      .catch(() => setIdentities([]));
  }, []);

  useEffect(() => {
    if (!linkedProvider || linkedChecked || identities === null) return;
    setLinkedChecked(true);
    const name = PROVIDER_LABEL[linkedProvider];
    setIdentityNote(
      identities.some((i) => i.provider === linkedProvider)
        ? { kind: "ok", text: `${name} is now connected to this SoundLayer ID.` }
        : { kind: "error", text: `${name} is not connected to this SoundLayer ID. The connection did not complete; you can try again below.` },
    );
  }, [linkedProvider, linkedChecked, identities]);

  useEffect(() => {
    loadIdentities();
    let active = true;
    enabledProviders().then((set) => active && setProviders(set));
    return () => {
      active = false;
    };
  }, [loadIdentities]);

  async function connect(provider: SocialProvider) {
    setIdentityBusy(provider);
    setIdentityNote(null);
    const message = await linkProvider(provider);
    if (message) {
      setIdentityNote({ kind: "error", text: message });
      setIdentityBusy(null);
    }
  }

  const load = useCallback(() => {
    api
      .me()
      .then(setMe)
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : "Could not load your account."));
  }, []);

  useEffect(load, [load]);

  async function submitLink(event: FormEvent) {
    event.preventDefault();
    if (code.length < 4) return;
    setLinking(true);
    setLinkResult(null);
    try {
      await api.linkSonos(code);
      setLinkResult({
        kind: "ok",
        text: "Code accepted. Go back to the Sonos app: it finishes the link on its own within a few seconds, then My Library appears under SoundLayer.",
      });
      setCode("");
      load();
    } catch (e: unknown) {
      setLinkResult({ kind: "error", text: e instanceof ApiError ? e.message : "Could not link." });
    } finally {
      setLinking(false);
    }
  }

  async function unlink() {
    if (!window.confirm("Unlink every Sonos system from this SoundLayer ID? They will need to link again.")) return;
    try {
      await api.unlinkSonos();
      setLinkResult(null);
      load();
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : "Could not unlink.");
    }
  }

  const sonos = me?.integrations.sonos;
  const households = sonos?.households ?? [];
  const linked = sonos?.status === "linked";

  return (
    <section className="stack">
      <div>
        <h1>Account</h1>
        <p className="lede">Your SoundLayer ID and where your library can play.</p>
      </div>
      {error && <Notice kind="error">{error}</Notice>}
      {!me && !error && <Progress label="Loading…" />}
      {me && (
        <div className="card stack">
          <h3>SoundLayer ID</h3>
          <dl className="kv">
            <dt>Email</dt>
            <dd>{me.email ?? session?.user.email ?? "—"}</dd>
            <dt>ID</dt>
            <dd className="mono">{me.id}</dd>
          </dl>
          <div className="row">
            <button className="btn" onClick={() => void signOut()}>
              Sign out
            </button>
          </div>
        </div>
      )}

      <h2>Sign-in methods</h2>
      <div className="card stack">
        <p className="small muted" style={{ margin: 0 }}>
          Ways to sign in to this SoundLayer ID. Connecting a provider adds a sign-in method to this same account;
          it never moves a library or Sonos link from another account. Removing a method is not available yet.
        </p>
        {identities === null && <Progress label="Loading sign-in methods…" />}
        {identities && (
          <div>
            {identities.map((identity) => {
              const provider = identity.provider;
              const label = provider === "email" ? "Email and password" : PROVIDER_LABEL[provider as SocialProvider] ?? provider;
              const detail = (identity.identity_data as { email?: string } | undefined)?.email;
              return (
                <div key={identity.identity_id} className="identity">
                  <div>
                    <strong>{label}</strong>
                    {detail && (
                      <div className="small muted">
                        {detail}
                        {provider === "apple" && /privaterelay\.appleid\.com$/i.test(detail) && " (Hide My Email relay)"}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {identities && SOCIAL_PROVIDERS.some((p) => providers.has(p) && !identities.some((i) => i.provider === p)) && (
          <div className="stack" style={{ gap: 10 }}>
            {SOCIAL_PROVIDERS.filter((p) => providers.has(p) && !identities.some((i) => i.provider === p)).map((p) => (
              <ProviderButton
                key={p}
                provider={p}
                action="Connect"
                busy={identityBusy === p}
                disabled={identityBusy !== null && identityBusy !== p}
                onClick={() => void connect(p)}
              />
            ))}
            <p className="small muted" style={{ margin: 0 }}>
              With Apple you can choose Hide My Email; Apple then gives SoundLayer a relay address, and your ID and library
              stay exactly the same.
            </p>
          </div>
        )}
        {identityNote && <Notice kind={identityNote.kind}>{identityNote.text}</Notice>}
      </div>

      <h2>Listen through</h2>
      <div className="card stack">
        <div className="row spread">
          <h3>Sonos</h3>
          <span className={`pill ${linked ? "pill-ok" : "pill-warn"}`}>{linked ? "Linked" : "Not linked"}</span>
        </div>
        {linked && (
          <ul style={{ margin: 0, paddingLeft: 18 }} className="small">
            {households.map((h) => (
              <li key={h.household_id}>
                Sonos system <span className="mono">{h.household_id.slice(0, 12)}…</span>
                {h.linked_at && ` · linked ${new Date(h.linked_at).toLocaleDateString()}`}
                {h.last_used_at && ` · last used ${new Date(h.last_used_at).toLocaleString()}`}
              </li>
            ))}
          </ul>
        )}
        <p className="small muted" style={{ margin: 0 }}>
          In the Sonos app, add the SoundLayer service and choose sign in. Sonos shows an 8-character code; enter it
          here. Your saved sounds then appear as “My Library” inside SoundLayer in the Sonos app.
        </p>
        <form className="row" onSubmit={submitLink} aria-busy={linking}>
          <div className="field" style={{ flex: "1 1 160px" }}>
            <label htmlFor="sonos-code">Code from the Sonos app</label>
            <input
              id="sonos-code"
              className="input mono"
              inputMode="text"
              autoCapitalize="characters"
              autoComplete="one-time-code"
              spellCheck={false}
              placeholder="ABCD2345"
              value={code}
              onChange={(e) => setCode(normalizeCode(e.target.value))}
              disabled={linking}
            />
          </div>
          <button className="btn btn-primary" type="submit" disabled={linking || code.length < 4} style={{ alignSelf: "end" }}>
            {linking ? "Linking…" : "Link"}
          </button>
        </form>
        {linkResult && <Notice kind={linkResult.kind === "ok" ? "ok" : "error"}>{linkResult.text}</Notice>}
        {linked && (
          <div className="row">
            <button className="btn btn-quiet btn-danger" onClick={unlink}>
              Unlink all Sonos systems
            </button>
          </div>
        )}
        <p className="small muted" style={{ margin: 0 }}>
          Linking lets that Sonos system browse and play your library. It cannot generate sounds, and it does not
          prove playback works on your speakers until you hear it.
        </p>
      </div>

      {FUTURE.map((t) => (
        <div key={t.key} className="card">
          <div className="row spread">
            <h3>{t.name}</h3>
            <span className="pill pill-off">Not yet available</span>
          </div>
          <p className="small muted" style={{ margin: "6px 0 0" }}>
            Planned. Not built.
          </p>
        </div>
      ))}
    </section>
  );
}
